import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Loader2, Mail, Lock, User, Phone } from "lucide-react";

function formatError(detail) {
  if (!detail) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function Auth() {
  const [mode, setMode] = useState("login"); // login | register
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const set = (key, val) => { setForm(prev => ({ ...prev, [key]: val })); setError(""); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      if (mode === "login") {
        await login(form.email, form.password);
        toast.success("Welcome back!");
      } else {
        if (form.password.length < 6) { setError("Password must be at least 6 characters"); setLoading(false); return; }
        await register(form.name, form.email, form.password, form.phone);
        toast.success("Account created!");
      }
      navigate("/account");
    } catch (err) {
      setError(formatError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-transition pt-[72px] min-h-screen bg-[#0c0c0c] flex items-center justify-center" data-testid="auth-page">
      <div className="w-full max-w-md px-6 py-16">
        <div className="text-center mb-10">
          <p className="overline mb-3">{mode === "login" ? "Welcome Back" : "Join Us"}</p>
          <h1 className="font-display text-3xl text-[#f8f5f0]">
            {mode === "login" ? "Sign In" : "Create Account"}
          </h1>
        </div>

        <div className="bg-[#161616] border border-[#b8966a]/10 p-8 md:p-10">
          {error && (
            <div className="bg-red-500/10 border border-red-400/30 p-3 mb-6" data-testid="auth-error">
              <p className="font-sans text-sm text-red-400">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="auth-form">
            {mode === "register" && (
              <>
                <div>
                  <label className="overline flex items-center gap-2 mb-2"><User size={12} /> Full Name</label>
                  <input type="text" required value={form.name} onChange={e => set("name", e.target.value)} placeholder="Your name"
                    className="w-full bg-[#0c0c0c] border border-[#b8966a]/20 px-4 py-3 text-[#f8f5f0] font-body focus:border-[#b8966a] transition-colors" data-testid="auth-name" />
                </div>
                <div>
                  <label className="overline flex items-center gap-2 mb-2"><Phone size={12} /> Phone (Optional)</label>
                  <input type="tel" value={form.phone} onChange={e => set("phone", e.target.value)} placeholder="(612) 547-9260"
                    className="w-full bg-[#0c0c0c] border border-[#b8966a]/20 px-4 py-3 text-[#f8f5f0] font-body focus:border-[#b8966a] transition-colors" data-testid="auth-phone" />
                </div>
              </>
            )}

            <div>
              <label className="overline flex items-center gap-2 mb-2"><Mail size={12} /> Email Address</label>
              <input type="email" required value={form.email} onChange={e => set("email", e.target.value)} placeholder="your@email.com"
                className="w-full bg-[#0c0c0c] border border-[#b8966a]/20 px-4 py-3 text-[#f8f5f0] font-body focus:border-[#b8966a] transition-colors" data-testid="auth-email" />
            </div>

            <div>
              <label className="overline flex items-center gap-2 mb-2"><Lock size={12} /> Password</label>
              <input type="password" required value={form.password} onChange={e => set("password", e.target.value)} placeholder={mode === "register" ? "Min 6 characters" : "Your password"}
                className="w-full bg-[#0c0c0c] border border-[#b8966a]/20 px-4 py-3 text-[#f8f5f0] font-body focus:border-[#b8966a] transition-colors" data-testid="auth-password" />
            </div>

            <button type="submit" disabled={loading} className="btn-primary w-full flex items-center justify-center gap-2 mt-2" data-testid="auth-submit">
              {loading ? <Loader2 className="animate-spin" size={16} /> : mode === "login" ? "Sign In" : "Create Account"}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="font-body text-sm text-[#a19d98]">
              {mode === "login" ? "Don't have an account?" : "Already have an account?"}
              <button onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
                className="text-[#b8966a] hover:text-[#cfae82] transition-colors ml-2 font-medium" data-testid="auth-toggle">
                {mode === "login" ? "Create one" : "Sign in"}
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
